import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CommonsService from 'features/commons/CommonsService';

import useErrorCatch from 'hooks/useErrorCatch';

import { SimpleOptionType } from 'types/filters';

import { SelectValue } from '../filter.types';

export interface UseCountryStateCityProps {
  countryValue?: string;
  cityValue?: string;
  stateValue?: string;
  onChangeCountry: (value?: string) => void;
  onChangeState: (value?: string) => void;
  onChangeCity: (value?: string) => void;
  countries: { code: string; label: string }[];
}

export const useCountryStateCity = ({
  countryValue,
  cityValue,
  stateValue,
  onChangeCountry,
  onChangeState,
  onChangeCity,
  countries
}: UseCountryStateCityProps) => {
  const { t } = useTranslation();
  const [states, setStates] = useState<SimpleOptionType[]>([]);
  const [cities, setCities] = useState<SimpleOptionType[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const catchError = useErrorCatch();

  const countriesOptions: SimpleOptionType[] = countries.map(
    ({ code, label }) => ({
      label,
      value: code,
      key: code
    })
  );

  const fetchCities = useCallback(
    async ({ country, state }: { country?: string; state?: string }) => {
      try {
        setIsLoadingCities(true);
        const response = state
          ? await CommonsService.getCitiesByState(state)
          : await CommonsService.getCitiesByCountry(country!);

        setCities(
          response.map((city) => ({
            label: city,
            value: city,
            key: city
          }))
        );
      } catch (error) {
        catchError({
          error,
          default: { message: t('error.message.fetchCities') }
        });
      } finally {
        setIsLoadingCities(false);
      }
    },
    [catchError, t]
  );

  const fetchStates = useCallback(
    async (country: string) => {
      try {
        setIsLoadingStates(true);
        const response = await CommonsService.getStatesByCountry(country);
        const mappedStates = Object.entries(response).map(([value, label]) => ({
          label,
          value,
          key: value
        }));
        setStates(mappedStates as unknown as SimpleOptionType[]);
        if (mappedStates.length === 0) {
          fetchCities({ country });
        }
      } catch (error) {
        catchError({
          error,
          default: { message: t('error.message.fetchStates') }
        });
      } finally {
        setIsLoadingStates(false);
      }
    },
    [catchError, t, fetchCities]
  );

  const handleCountryChange = (value: SelectValue) => {
    const country = value as string;
    onChangeCountry(country);
    onChangeState(undefined);
    onChangeCity(undefined);
    setStates([]);
    setCities([]);
    if (country) {
      fetchStates(country);
    }
  };

  const handleStateChange = (value: SelectValue) => {
    const state = value as string;
    onChangeState(state);
    onChangeCity(undefined);
    setCities([]);
    fetchCities({ state });
  };

  const handleCityChange = (value: SelectValue) => {
    onChangeCity(value as string);
  };

  useEffect(() => {
    if (countryValue) {
      fetchStates(countryValue);
    }
    if (stateValue) {
      fetchCities({ state: stateValue });
    } else if (countryValue) {
      fetchCities({ country: countryValue });
    }
  }, [countryValue, stateValue, fetchStates, fetchCities]);

  const hasStates = states.length > 0;
  const hasCities = cities.length > 0;
  const statesLoaded = !isLoadingStates && !!countryValue;

  const isShowState = !!countryValue && statesLoaded && hasStates;

  const isShowCity =
    !!countryValue && statesLoaded && (!hasStates || !!stateValue) && hasCities;

  return {
    country: countryValue,
    state: stateValue,
    city: cityValue,
    states,
    cities,
    countriesOptions,
    handleCountryChange,
    handleStateChange,
    handleCityChange,
    isShowState,
    isShowCity,
    hasStates,
    isLoadingStates,
    isLoadingCities
  };
};
